import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography } from '@mui/material';
import PrivateRoute from 'Components/Route/PrivateRoute';
import RecipeForm from 'Components/RecipeForm/RecipeForm';
import ButtonAppBar from 'Layouts/ButtonAppBar/ButtonAppBar';
import { useNotification } from 'Hooks/useNotification';
import { recipeService } from 'Services/recipeService';
import { CreateRecipeInput } from 'Types/api';
import { ROUTES } from 'Utils/routes';

const CreateRecipe = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [recipeId, setRecipeId] = useState<string | null>(null);
  const isCreatingRef = useRef(false);

  const handleSubmit = async (input: CreateRecipeInput) => {
    try {
      if (recipeId) {
        // Recipe was already created via auto-save, just update it
        await recipeService.update(recipeId, input);
      } else {
        await recipeService.create(input);
      }
      showNotification('Recipe created successfully!', 'success');
      navigate(ROUTES.DASHBOARD);
    } catch {
      showNotification('Failed to create recipe. Please try again.');
    }
  };

  const handleAutoSave = useCallback(
    async (input: CreateRecipeInput) => {
      // If we already have a recipe ID, update it
      if (recipeId) {
        await recipeService.update(recipeId, input);
        return;
      }

      // Prevent concurrent create requests
      if (isCreatingRef.current) {
        return;
      }

      // First auto-save: create the recipe
      isCreatingRef.current = true;
      try {
        const created = await recipeService.create(input);
        setRecipeId(created.id);
        // Update the URL to reflect that we're now editing
        window.history.replaceState(null, '', `/recipes/${created.id}/edit`);
      } finally {
        isCreatingRef.current = false;
      }
    },
    [recipeId],
  );

  return (
    <PrivateRoute>
      <ButtonAppBar />
      <Container maxWidth='md'>
        <Box component='main' sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2 } }}>
          <Typography variant='h4' component='h1' gutterBottom>
            Create Recipe
          </Typography>
          <RecipeForm
            onSubmit={handleSubmit}
            submitLabel='Create Recipe'
            submittingLabel='Creating...'
            onAutoSave={handleAutoSave}
            autoSaveEnabled={true}
            onImageUploaded={handleAutoSave}
          />
        </Box>
      </Container>
    </PrivateRoute>
  );
};

export default CreateRecipe;
